import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

const PRIMARY = 'var(--color-primary)';
const BORDER = 'var(--color-border)';
const TEXT_MUTED = 'var(--color-text-muted)';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 8px' }}>{title}</h2>
    <div style={{ fontSize: 14, color: 'var(--color-text)', lineHeight: 1.7 }}>{children}</div>
  </section>
);

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div style={{ backgroundColor: 'var(--color-background)', minHeight: '100vh', padding: '24px 16px' }}>
      <Helmet>
        <title>Terms of Service | Phrenos</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: PRIMARY, fontSize: 13, fontWeight: 600, padding: 0,
            marginBottom: 20, display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          ← Back
        </button>

        <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--color-text)', margin: '0 0 4px' }}>
          Terms of Service
        </h1>
        <p style={{ fontSize: 12, color: TEXT_MUTED, margin: '0 0 32px' }}>
          Last updated: March 15, 2026
        </p>

        <div style={{
          backgroundColor: 'var(--color-surface)',
          border: `1px solid ${BORDER}`,
          borderRadius: 16,
          padding: '28px 24px',
        }}>
          <Section title="Acceptance of Terms">
            <p style={{ margin: 0 }}>
              By accessing or using Phrenos at <strong>phrenos.app</strong> ("the Service"), you agree to be bound
              by these Terms of Service. If you do not agree to these terms, do not use the Service.
            </p>
          </Section>

          <Section title="Description of Service">
            <p style={{ margin: 0 }}>
              Phrenos is a free vocabulary and quantitative study tool designed to help users prepare for
              standardized tests including the GRE, SAT, and GMAT. Features include flashcards, synonym
              matching, spaced repetition, hard-term quizzes, and quant problem tracking.
              Phrenos is not affiliated with ETS, College Board, GMAC, LSAC, or any other test publisher.
            </p>
          </Section>

          <Section title="Account Registration">
            <p style={{ margin: '0 0 8px' }}>To save your progress, you must create an account using a valid email address. You agree to:</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Provide accurate and complete information when registering</li>
              <li>Keep your password secure and not share your account with others</li>
              <li>Notify us immediately if you suspect unauthorized access to your account</li>
            </ul>
            <p style={{ margin: '10px 0 0' }}>
              You are responsible for all activity that occurs under your account.
            </p>
          </Section>

          <Section title="Acceptable Use">
            <p style={{ margin: '0 0 8px' }}>You agree not to:</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Scrape, copy, or redistribute the word lists or content without permission</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Use automated tools to access the Service in a manner that exceeds reasonable personal use</li>
            </ul>
          </Section>

          <Section title="Intellectual Property">
            <p style={{ margin: 0 }}>
              The Phrenos name, logo, and app interface are our intellectual property. The vocabulary word
              lists are compiled from public domain sources. GRE®, SAT®, GMAT®, LSAT®, and MCAT® are
              registered trademarks of their respective owners — use of these names on Phrenos is for
              descriptive purposes only and does not imply affiliation or endorsement.
            </p>
          </Section>

          <Section title="User Content">
            <p style={{ margin: 0 }}>
              When you submit bug reports or word issue reports through the app, you grant us a
              non-exclusive, royalty-free license to use that feedback to improve the Service.
              We will not publish or attribute your feedback without your permission.
            </p>
          </Section>

          <Section title="Disclaimer of Warranties">
            <p style={{ margin: 0 }}>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE,
              OR THAT THE CONTENT IS ACCURATE OR COMPLETE. YOUR USE OF THE SERVICE IS AT YOUR OWN RISK.
              WE MAKE NO GUARANTEE THAT USING PHRENOS WILL IMPROVE YOUR TEST SCORES.
            </p>
          </Section>

          <Section title="Limitation of Liability">
            <p style={{ margin: 0 }}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, PHRENOS AND ITS OPERATORS SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF OR
              INABILITY TO USE THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING FROM THESE TERMS SHALL NOT EXCEED $10.
            </p>
          </Section>

          <Section title="Termination">
            <p style={{ margin: 0 }}>
              We reserve the right to suspend or terminate your account at any time for violation of these
              Terms or for any other reason at our sole discretion. You may delete your account at any time
              by contacting us at <strong>support@phrenos.app</strong>.
            </p>
          </Section>

          <Section title="Changes to These Terms">
            <p style={{ margin: 0 }}>
              We may update these Terms from time to time. We will notify registered users of material
              changes by updating the "Last updated" date above. Continued use of the Service after
              changes constitutes acceptance of the updated Terms.
            </p>
          </Section>

          <Section title="Governing Law">
            <p style={{ margin: 0 }}>
              These Terms are governed by the laws of the United States. Any disputes arising from these
              Terms or your use of the Service shall be resolved through binding arbitration or in the
              courts of competent jurisdiction in the United States.
            </p>
          </Section>

          <Section title="Contact">
            <p style={{ margin: 0 }}>
              Questions about these Terms? Contact us at <strong>support@phrenos.app</strong>.
            </p>
          </Section>
        </div>

        <p style={{ fontSize: 11, color: TEXT_MUTED, textAlign: 'center', marginTop: 24 }}>
          © 2026 Phrenos. Not affiliated with ETS, College Board, GMAC, or LSAC.
        </p>
      </div>
    </div>
  );
}
